import { PathfindingHandler } from "../modules/pathfinding/PathfindingHandler";
import { findDropEndpoint, getPossibleEndpointPositions } from "../components/cell/grid-cell.utils";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description Utils used for connector related operations
 */
export class ConnectorsUtils {
    /**
     * @description - Redraws provided cells
     * @param {String[]} cells - grid cell uuid array to be redraw.
     * @param { ConnectingLineHandler } lineHandler  - ConnectingLineHandler instance
     * @returns { void }
     */
    redrawAffectedCells(cells, lineHandler) {
        for (let i = 0; i < cells.length; i++) {
            lineHandler.cells.get(cells[i]).ref.draw();
        }
    }

    /**
     * @description Returns a array with unique elements created from the two parameters provided
     * @param { GridCell[] } dropCellConnectorsData
     * @param { GridCell[] } movedCellData
     * @returns { GridCell[] }
     */
    getAllAffectedPassthroughCellsByMove(dropCellConnectorsData, movedCellData) {
        return [...dropCellConnectorsData.passThroughCells, ...movedCellData.passThroughCells]
            .filter((cellUuid, index, arr) => arr.indexOf(cellUuid) === index);
    }

    /**
     * @description - Identifies the connectors that pass through the provided cell and the cells that each identified connector passes through.
     * @param { ConnectingLineHandler } lineHandler - ConnectingLineHandler instance
     * @param { GridCell } cell - Drop cell (called from @PluginDragHandler dropCell function)
     * @returns { Object } - {pathfindingData, passThroughCells} pathfindingData- the connectors that pass through the cell
     *                                                           passThroughCells - the cells each connector pass through
     */
    getConnectorsPassingThroughDropCell(lineHandler, cell) {
        if (lineHandler.passedThroughCells.indexOf(cell.uuid) !== -1) {
            const connectors = [];
            lineHandler.connectors.forEach(connector => {
                if (connector.passesThrough.indexOf(cell.uuid) !== -1) {
                    connectors.push(connector);
                }
            });
            const pathfindingData = [];
            const passThroughCells = [];
            for (let i = 0; i < connectors.length; i++) {
                const line = connectors[i];
                passThroughCells.push(...line.passesThrough);
                const from = lineHandler.cells.get(line.from).ref;
                const to = lineHandler.cells.get(line.to).ref;
                const currentPath = line.instance.layout.path;
                const points = this.extractCurrentEndpointsFromLine(from, to, currentPath[0], currentPath[currentPath.length - 1]);
                pathfindingData.push({
                    from, to, points, uuid: line.instance.uuid
                });
            }
            return { pathfindingData, passThroughCells };
        }
        return { pathfindingData: [], passThroughCells: [] };
    }

    /**
     * @description - Identifies the connectors that pass through the provided cell and the cells that each identified connector passes through.
     * @param { ConnectingLineHandler } lineHandler - ConnectingLineHandler instance
     * @param { String } oldCellUuid - old cell from which the plugin was moved
     * @param { String } newCellUuid  - new cell to which the plugin was dropped
     * @returns { Object } - pathFindingData, passThroughCells
     */
    getPluginConnectorsFromMovedCell(lineHandler, oldCellUuid, newCellUuid) {
        const { cells } = lineHandler;
        const oldCell = lineHandler.cells.get(oldCellUuid);
        const newCell = lineHandler.cells.get(newCellUuid);
        const passThroughCells = [];
        const pathFindingData = { outgoing: [], incoming: [] };
        for (let i = 0; i < oldCell.outgoing.length; i++) {
            const line = lineHandler.connectors.get(oldCell.outgoing[i]);
            const from = newCell.ref; const to = cells.get(line.to).ref; const
                { path } = line.instance.layout;
            const points = this.extractCurrentEndpointsFromLine(oldCell.ref, to, path[0], path[path.length - 1]);
            const startToSet = {};
            startToSet[Object.keys(points.start)[0]] = this.getPossibleEndpointsOfCell(newCell.ref)[Object.keys(points.start)[0]];
            points.start = startToSet;
            passThroughCells.push(...line.passesThrough);
            pathFindingData.outgoing.push({
                from, to, points, fromChanged: true, uuid: line.instance.uuid
            });
        }
        for (let x = 0; x < oldCell.incoming.length; x++) {
            const incomingLine = lineHandler.connectors.get(oldCell.incoming[x]);
            const incomingFrom = cells.get(incomingLine.from).ref; const incomingTo = cells.get(newCellUuid).ref; const
                { path } = incomingLine.instance.layout;
            // get the old possible endpoints
            const points = this.extractCurrentEndpointsFromLine(incomingFrom, oldCell.ref, path[0], path[path.length - 1]);
            const endToSet = {};
            endToSet[Object.keys(points.end)[0]] = this.getPossibleEndpointsOfCell(newCell.ref)[Object.keys(points.end)[0]];
            points.end = endToSet;
            passThroughCells.push(...incomingLine.passesThrough);
            pathFindingData.incoming.push({
                from: incomingFrom, to: incomingTo, toChanged: true, points, uuid: incomingLine.instance.uuid
            });
        }
        return { pathFindingData, passThroughCells };
    }

    /**
     * @description Reduces the two sources of connector uuids to uuids and returns it
     * @param { Object } passthroughDropCellConnectors  - Passthrough drop cell connectors
     * @param { Object } droppedPluginConnectors - Dropped plugin data including incoming and outgoing connectors
     * @returns { String[] }
     */
    getAllConnectorsUuidsInNeedOfUpdate(passthroughDropCellConnectors, droppedPluginConnectors) {
        const toReturn = [];
        const passthroughDroppcellConnectorsUuids = passthroughDropCellConnectors.pathfindingData.map(data => data.uuid);
        const allDroppedPluginConnectors = [...droppedPluginConnectors.pathFindingData.incoming, ...droppedPluginConnectors.pathFindingData.outgoing].map(data => data.uuid);
        toReturn.push(...passthroughDroppcellConnectorsUuids, ...allDroppedPluginConnectors);
        return toReturn;
    }

    /**
     * @description - Recalculates the path of all affected connectors
     * @param {GridCells[][]} grid - the generated grid matrix holding the GridCelReferences
     * @param {*} connectorsPassingThroughDropCell - Passthrough drop cell connectors
     * @param {*} droppedPluginConnectors - Dropped plugin data including incoming and outgoing connectors
     * @returns { void }
     */
    recalculateAllAffectedPaths(grid, connectorsPassingThroughDropCell, droppedPluginConnectors) {
        const connectorsInNeedOfUpdate = this.getAllConnectorsUuidsInNeedOfUpdate(connectorsPassingThroughDropCell, droppedPluginConnectors);
        connectorsPassingThroughDropCell.pathfindingData.forEach(connectorData => {
            const data = this.recalculateAffectedPath(connectorData, grid, connectorsInNeedOfUpdate);
            connectorData.path = data.path;
            connectorData.endSide = data.endSide;
            const connectorIndex = connectorsInNeedOfUpdate.indexOf(connectorData.uuid);
            connectorsInNeedOfUpdate.splice(connectorIndex, 1);
        });
        droppedPluginConnectors.pathFindingData.outgoing.forEach(connectorData => {
            const data = this.recalculateAffectedPath(connectorData, grid, connectorsInNeedOfUpdate);
            connectorData.path = data.path;
            connectorData.endSide = data.endSide;
            const connectorIndex = connectorsInNeedOfUpdate.indexOf(connectorData.uuid);
            connectorsInNeedOfUpdate.splice(connectorIndex, 1);
        });
        droppedPluginConnectors.pathFindingData.incoming.forEach(connectorData => {
            const data = this.recalculateAffectedPath(connectorData, grid, connectorsInNeedOfUpdate);
            connectorData.path = data.path;
            connectorData.endSide = data.endSide;
            const connectorIndex = connectorsInNeedOfUpdate.indexOf(connectorData.uuid);
            connectorsInNeedOfUpdate.splice(connectorIndex, 1);
        });
    }

    /**
     * @description Recalculates the provided path
     * @param { Object } pathData - {from,to, points} from cell, to cell, cell points from whitch the line drag and drop happenned
     * @param { GridCell[] } grid - the generated grid matrix holding the GridCell References
     * @param { Array } linesToIgnore - Connectors to ignore when generating the paths
     * @returns { Object } - new generated by pathfinding handler path
     */
    recalculateAffectedPath(pathData, grid, linesToIgnore) {
        const pathFinding = new PathfindingHandler(grid, pathData.from, pathData.to, pathData.points, linesToIgnore);
        return pathFinding.search();
    }

    /**
     * @description Updates all connectors path that pass through the provided cell
     * @param { GridCell[] } grid - the generated grid matrix holding the GridCell References
     * @param { ConnectingLineHandler } lineHandler - ConnectingLineHandler instance
     * @param { GridCell } cell - grid cell reference
     * @param { Boolean } returnPathfindingData - indicates if the data should be gathered and returned
     * @returns { void | Object } - if is object {from, to, points, uuid: line.instance.uuid}
     */
    performUpdatesOnConnector(grid, lineHandler, cell, returnPathfindingData) {
        const cellsToRedraw = [];
        if (lineHandler.passedThroughCells.indexOf(cell.uuid) !== -1) {
            const connectors = [];
            lineHandler.connectors.forEach(connector => {
                if (connector.passesThrough.indexOf(cell.uuid) !== -1) {
                    cellsToRedraw.push(...connector.passesThrough);
                    connectors.push(connector);
                }
            });
            const pathfindingData = [];
            for (let i = 0; i < connectors.length; i++) {
                const line = connectors[i];
                const from = lineHandler.cells.get(line.from).ref;
                const to = lineHandler.cells.get(line.to).ref;
                const currentPath = line.instance.layout.path;
                const points = this.extractCurrentEndpointsFromLine(from, to, currentPath[0], currentPath[currentPath.length - 1]);
                if (returnPathfindingData) {
                    pathfindingData.push({
                        from, to, points, uuid: line.instance.uuid
                    });
                    continue;
                }
                const pathFinding = new PathfindingHandler(grid, from, to, points);
                const path = pathFinding.search();
                cellsToRedraw.push(...lineHandler.updateConnectorPathV2(line.instance.uuid, path, grid));
            }
            cellsToRedraw.filter((elem, index, arr) => arr.indexOf(elem) === index).forEach(cellUuid => {
                lineHandler.cells.get(cellUuid).ref.draw();
            });
            return pathfindingData;
        }
    }

    /**
     * @description - Returns the cell endpoints
     * @param {GridCell} cell - the cell to get the endpoints from
     * @returns {Object} {LEFT,RIGHT,TOP,BOTTOM}
     */
    getPossibleEndpointsOfCell(cell) {
        return getPossibleEndpointPositions(cell.getContainerInstance(), null, cell);
    }

    /**
     * @description - Extracts the start and end endpoints
     * @param { GridCell } fromCell - From the cell the dragging started happening
     * @param { GridCell } toCell -  The cell the drop happened at
     * @param { Object } startCoord  - {x,y} object indicating the mouse position at the START of the action
     * @param { Object } endCoord - {x,y} object indicating the mouse position at the END of the action
     * @returns { Object } {start,end} each having a property named after the side of the endpoint and having as a value the {x,y} of that endpoint
     */
    extractCurrentEndpointsFromLine(fromCell, toCell, startCoord, endCoord) {
        const instanceUuid = getInstanceIDFromCanvasInstance(fromCell.ctx);
        const start = findDropEndpoint(startCoord, getPossibleEndpointPositions(fromCell.getContainerInstance(), null, fromCell), null, instanceUuid);
        const end = findDropEndpoint(endCoord, getPossibleEndpointPositions(toCell.getContainerInstance(), null, toCell), null, instanceUuid);
        const obj = { start: {}, end: {} };
        obj.start[start.side] = { x: start.endpoint.x, y: start.endpoint.y };
        obj.end[end.side] = { x: end.endpoint.x, y: end.endpoint.y };
        return obj;
    }

    /**
     * @description Gets the cells uuids for the provided contentUuids array
     * @param {String[]} contentUuids - Plugin uuids array
     * @param { ConnectingLineHandler } lineHandler -  ConnectingLineHandler instance
     * @returns { Object } - sa key-value object where the key is the plugin uuid and the value is the cell uuid hosting the plugin
     */
    getCellsFromContentUuids(contentUuids, lineHandler) {
        const keys = lineHandler.cells.keys();
        const toReturn = {};
        for (const key of keys) {
            if (Object.keys(toReturn).length === contentUuids.length) break;
            const cell = lineHandler.cells.get(key);
            if (!cell.ref.hasContent()) continue;
            const cellContent = cell.ref.getContentInstance();
            const index = contentUuids.indexOf(cellContent.uuid);
            if (index !== -1) {
                toReturn[contentUuids[index]] = key;
                continue;
            }
        }
        return toReturn;
    }
}
